import type { Photo, RunsIndex } from './types';
import { formatDistance, formatDuration } from './format';
import { config } from '../config';

interface Props {
  index: RunsIndex;
  photos?: Photo[];
  onOpenPhoto?: (index: number) => void;
  showPhotoPins?: boolean;
  onTogglePhotoPins?: () => void;
}

export function SummaryPanel({
  index,
  photos = [],
  onOpenPhoto,
  showPhotoPins = false,
  onTogglePhotoPins
}: Props) {
  const { count, distance_m, duration_s } = index.totals;

  return (
    <div className="fixed top-3 left-3 md:top-5 md:left-5 z-10 max-w-[calc(100vw-24px)] md:max-w-[320px]">
      <div className="glass rounded-2xl shadow-panel px-5 py-4 md:px-5 md:py-4">
        {/* Compact mobile layout */}
        <div className="flex items-center gap-2.5 md:hidden">
          <span className="w-1.5 h-1.5 rounded-full bg-accent shrink-0" />
          <span className="font-display text-[15px] leading-none text-ink-900 whitespace-nowrap">
            Run with <span className="text-accent italic">Krutik Patel</span>
          </span>
          {onTogglePhotoPins && (
            <PinToggle
              on={showPhotoPins}
              onClick={onTogglePhotoPins}
              className="ml-1"
            />
          )}
        </div>

        {/* Desktop layout */}
        <div className="hidden md:block">
          <h1 className="font-display text-[28px] leading-[1.1] text-ink-900 mb-3">
            Run with{' '}
            <span className="text-accent italic">Krutik Patel</span>.
          </h1>
          <div className="h-px bg-gradient-to-r from-accent/40 via-ink-100 to-transparent mb-5" />
          <div className="grid grid-cols-3 gap-3">
            <Metric label="Runs" value={String(count)} />
            <Metric label="Distance" value={formatDistance(distance_m, config.distanceUnit)} />
            <Metric label="Time" value={formatDuration(duration_s)} />
          </div>

          {photos.length > 0 && onOpenPhoto && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <div className="stat-label">Photos</div>
                {onTogglePhotoPins && (
                  <PinToggle
                    on={showPhotoPins}
                    onClick={onTogglePhotoPins}
                    label="Pins on map"
                  />
                )}
              </div>
              <ul className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-0.5">
                {photos.map((p, i) => (
                  <li key={p.id} className="shrink-0">
                    <button
                      type="button"
                      onClick={() => onOpenPhoto(i)}
                      aria-label={`Open photo ${i + 1} of ${photos.length}`}
                      className="block h-12 w-12 overflow-hidden rounded-md bg-ink-100 hover:opacity-90 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                      <img
                        src={p.thumbUrl}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PinToggle({
  on,
  onClick,
  label,
  className = ''
}: {
  on: boolean;
  onClick: () => void;
  label?: string;
  className?: string;
}) {
  const a11yLabel = on ? 'Hide photo pins on map' : 'Show photo pins on map';
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={a11yLabel}
      title={a11yLabel}
      onClick={onClick}
      className={`group inline-flex shrink-0 items-center gap-1.5 focus:outline-none ${className}`}
    >
      {label && (
        <span className="text-[10px] uppercase tracking-wider font-medium text-ink-500 group-hover:text-ink-700 transition-colors">
          {label}
        </span>
      )}
      <span
        className={`relative inline-flex h-[18px] w-[32px] items-center rounded-full transition-colors duration-200 group-focus-visible:ring-2 group-focus-visible:ring-accent group-focus-visible:ring-offset-1 ${
          on
            ? 'bg-accent'
            : 'bg-ink-300 group-hover:bg-ink-500'
        }`}
      >
        <span
          className={`absolute inline-flex h-[14px] w-[14px] items-center justify-center rounded-full bg-white shadow-[0_1px_3px_rgba(11,13,16,0.3)] transition-transform duration-200 ease-out ${
            on ? 'translate-x-[16px]' : 'translate-x-[2px]'
          }`}
        >
          <svg
            width="8"
            height="8"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
            className={on ? 'text-accent' : 'text-ink-700'}
          >
            <path d="M12 2C7.58 2 4 5.58 4 10c0 5.5 8 12 8 12s8-6.5 8-12c0-4.42-3.58-8-8-8zm0 11a3 3 0 110-6 3 3 0 010 6z" />
          </svg>
        </span>
      </span>
    </button>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="stat-label">{label}</div>
      <div className="mt-1 font-mono text-[15px] text-ink-900 tracking-tight">{value}</div>
    </div>
  );
}
